import React, { useState, memo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, BookOpenIcon, FingerprintIcon, SlidersIcon, TrendingUpIcon, ShieldCheckIcon, UsersIcon, BarChart3Icon, CheckCircleIcon, HelpCircleIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export const MethodologyAccordion: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isStimulusExpanded, setIsStimulusExpanded] = useState(false);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden print:shadow-none print:border print:border-gray-300 print:break-inside-avoid" data-id="element-394">
      {/* Trigger Button */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full p-6 md:p-8 flex items-center justify-between hover:bg-gray-50/50 transition-colors text-left group cursor-pointer" data-id="element-395">
        <div className="flex items-start md:items-center space-x-4" data-id="element-396">
          <div className="p-2 bg-[#1E90FF]/10 rounded-lg group-hover:bg-[#1E90FF]/20 transition-colors" data-id="element-397">
            <BookOpenIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-398" />
          </div>
          <div data-id="element-399">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900 group-hover:text-[#1E90FF] transition-colors" data-id="element-400">
              Methodology: How We Measure Resonance
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-401">
              {isExpanded ? 'Click to collapse' : 'Click to expand'} •
              Understanding the science behind your +33% brand lift
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-402">
          <span className="text-xs font-medium text-gray-500 hidden sm:block" data-id="element-403">
            {isExpanded ? 'Hide' : 'Show'}
          </span>
          {isExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#1E90FF] flex-shrink-0 ml-4 group-hover:scale-110 transition-transform" data-id="element-404" /> : <ChevronDownIcon className="h-5 w-5 text-[#1E90FF] flex-shrink-0 ml-4 group-hover:scale-110 transition-transform" data-id="element-405" />}
        </div>
      </button>

      <AnimatePresence data-id="element-406">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-407">
            <div className="p-6 md:p-8 pt-0 border-t border-gray-100 bg-gray-50/30 space-y-8" data-id="element-408">
              {/* Executive Summary */}
              <div className="bg-white p-6 rounded-xl border-l-4 border-[#1E90FF] shadow-sm" data-id="element-409">
                <div className="flex items-center space-x-2 mb-3" data-id="element-410">
                  <ClockIcon className="h-4 w-4 text-[#1E90FF]" data-id="element-411" />
                  <span className="text-xs font-bold text-[#1E90FF] uppercase tracking-wide" data-id="element-412">
                    30-Second Summary
                  </span>
                </div>
                <p className="text-base text-gray-700 leading-relaxed font-medium" data-id="element-413">
                  We observed 279,000+ unique individuals from your target
                  audience across three distinct phases: before, during, and
                  after your TV campaign. Using a constant neutral stimulus
                  across all phases, we measured how TV media pressure
                  influenced behavioral engagement. The result: a{' '}
                  <span className="text-[#1E90FF] font-bold" data-id="element-414">
                    validated +33% increase
                  </span>{' '}
                  in brand interaction during the campaign flight (97.5%
                  confidence), with a promising{' '}
                  <span className="text-[#1E90FF] font-bold" data-id="element-415">
                    +14% sustained elevation
                  </span>{' '}
                  post-campaign (directional evidence).
                </p>
              </div>

              {/* Three Core Principles */}
              <div data-id="element-416">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-417">
                  The Three Core Principles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-418">
                  {/* The Signal */}
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-419">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-420">
                      <FingerprintIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-421" />
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-422">
                        The Signal
                      </h4>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-423">
                      Actual vs. Claimed Behavior
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-424">
                      Traditional research asks people what they remember
                      seeing. Brandpulse tracks what they subconsciously do. We
                      observed 279,000+ unique individuals in their natural
                      viewing environment, capturing authentic behavioral
                      responses rather than claimed recall.
                    </p>
                  </div>

                  {/* The Constant Stimulus */}
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-425">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-426">
                      <SlidersIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-427" />
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-428">
                        The Constant Stimulus
                      </h4>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-429">
                      Isolating TV Impact
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-430">
                      To isolate the pure impact of your TV campaign, we
                      deployed a neutral, constant brand stimulus across all
                      measurement phases. Think of it as a thermometer: the
                      stimulus measures the temperature, while your TV campaign
                      is the heat source.
                    </p>

                    {/* Expandable Stimulus Image */}
                    <button onClick={() => setIsStimulusExpanded(!isStimulusExpanded)} className="w-full text-left flex items-center justify-between text-sm font-medium text-[#1E90FF] hover:text-[#1E90FF]/80 transition-colors" data-id="element-431">
                      <span data-id="element-432">
                        {isStimulusExpanded ? 'Hide' : 'View'} Constant Stimulus
                      </span>
                      {isStimulusExpanded ? <ChevronUpIcon className="h-4 w-4" data-id="element-433" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-434" />}
                    </button>

                    <AnimatePresence data-id="element-435">
                      {isStimulusExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-436">
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-437">
                            <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm" data-id="element-438">
                              {/* Illustrative badge */}
                              <div className="absolute top-3 left-3 z-10 px-2 py-1 rounded-md bg-white border border-gray-200 text-[9px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-439">
                                Illustrative example
                              </div>

                              {/* Neutral ad layout — name and logo only */}
                              <div className="absolute inset-0 flex items-center justify-center" data-id="element-440">
                                <div className="flex items-center gap-3" data-id="element-441">
                                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center" data-id="element-442">
                                    <span className="text-white font-black text-xl" data-id="element-443">
                                      X
                                    </span>
                                  </div>
                                  <span className="text-gray-900 font-bold text-2xl tracking-tight" data-id="element-444">
                                    BrandX
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-3 text-center italic" data-id="element-445">
                              Illustrative mockup for a fictional brand · Shown
                              consistently across all three measurement phases
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* The Metric */}
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-446">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-447">
                      <TrendingUpIcon className="h-6 w-6 text-[#1E90FF]" data-id="element-448" />
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-449">
                        The Metric
                      </h4>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-450">
                      Uplift as Activation
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-451">
                      Uplift is not passive awareness, it's active engagement. A
                      +33% uplift means your target audience demonstrated 33%
                      more interaction behavior with your brand compared to the
                      pre-campaign baseline.
                    </p>
                  </div>
                </div>
              </div>

              {/* Three-Phase Measurement Design */}
              <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-452">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-453">
                  Three-Phase Measurement Design
                </h3>
                <div className="space-y-4" data-id="element-454">
                  <div className="flex items-start space-x-3" data-id="element-455">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold" data-id="element-456">
                      1
                    </div>
                    <div data-id="element-457">
                      <p className="text-sm font-bold text-gray-900" data-id="element-458">
                        Baseline (Pre-Campaign)
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-459">
                        Measured target audience behavior before TV campaign
                        launch. Established natural engagement levels without
                        media pressure. Period: December 9-14
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-460">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1E90FF]/10 text-[#1E90FF] flex items-center justify-center text-sm font-bold" data-id="element-461">
                      2
                    </div>
                    <div data-id="element-462">
                      <p className="text-sm font-bold text-gray-900" data-id="element-463">
                        Flight (During Campaign)
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-464">
                        Measured same target audience while TV ads were airing.
                        Captured behavioral response under active media
                        pressure. Period: December 15-31
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-465">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold" data-id="element-466">
                      3
                    </div>
                    <div data-id="element-467">
                      <p className="text-sm font-bold text-gray-900" data-id="element-468">
                        Post-Flight (After Campaign)
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-469">
                        Measured target audience after TV campaign concluded.
                        Assessed sustained impact and brand equity retention.
                        Period: January 1-7
                      </p>
                    </div>
                  </div>
                </div>

                {/* Audience Exclusion Feature */}
                <div className="mt-6 p-4 bg-[#1E90FF]/5 rounded-lg border border-[#1E90FF]/20" data-id="element-470">
                  <div className="flex items-start space-x-3" data-id="element-471">
                    <ShieldCheckIcon className="h-5 w-5 text-[#1E90FF] flex-shrink-0 mt-0.5" data-id="element-472" />
                    <div data-id="element-473">
                      <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-474">
                        Critical Design Feature: Audience Exclusion
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-475">
                        To ensure that observed uplift stems from TV media
                        pressure, not from repeated exposure to our measurement
                        stimulus, we exclude individuals from one phase when
                        observing subsequent phases. This prevents recognition
                        bias and guarantees that behavioral changes reflect
                        genuine campaign impact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistical Power Through Scale */}
              <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-476">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-477">
                  Why Large-Scale Observation Matters
                </h3>
                <p className="text-sm font-semibold text-gray-900 mb-3" data-id="element-478">
                  Statistical Power Through Scale
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-479">
                  Brandpulse operates on a binary response model: we observe
                  whether individuals engage with the brand stimulus or do not.
                  Both outcomes are valuable data points.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-480">
                  With a traditional research panel of 1,000 people, subtle
                  behavioral shifts are statistically invisible, the sample is
                  too small to detect meaningful change. By observing 279,000+
                  unique individuals, even modest shifts in engagement become
                  statistically validated and actionable.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-481">
                  This is the advantage of mass-audience observation: we measure
                  the entire market's response, not a small sample's opinion.
                </p>
              </div>

              {/* Statistical Validation with Confidence Levels */}
              <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-482">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-483">
                  Statistical Validation & Confidence Levels
                </h3>
                <div className="space-y-4" data-id="element-484">
                  <div data-id="element-485">
                    <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-486">
                      Bayesian Analysis
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-487">
                      We employ Bayesian structural time-series modeling to
                      calculate uplift with precision. This approach compares
                      observed behavioral patterns across the three phases,
                      isolating the campaign's causal impact from external
                      market noise.
                    </p>
                  </div>

                  {/* Campaign Period Confidence */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-488">
                    <div className="flex items-start space-x-3" data-id="element-489">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-490" />
                      <div data-id="element-491">
                        <p className="text-sm font-bold text-gray-900 mb-1" data-id="element-492">
                          Campaign Period (+33% Uplift): 97.5% Confidence
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-493">
                          We can state with 97.5% certainty that your TV
                          campaign caused the +33% behavioral uplift during the
                          flight period. This level of confidence meets CFO
                          standards for marketing investment justification.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Post-Flight Confidence */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-id="element-494">
                    <div className="flex items-start space-x-3" data-id="element-495">
                      <AlertCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-496" />
                      <div data-id="element-497">
                        <p className="text-sm font-bold text-gray-900 mb-1" data-id="element-498">
                          Post-Flight Period (+14% Sustained): Directional
                          Evidence
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2" data-id="element-499">
                          The +14% sustained lift observed in the two weeks
                          following campaign conclusion represents strong
                          directional evidence of lasting brand equity. However,
                          this post-flight period is shorter than ideal for
                          statistical validation (larger margin of error).
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-500">
                          <span className="font-semibold text-gray-900" data-id="element-501">
                            For future tests:
                          </span>{' '}
                          Extending the post-flight observation window increases
                          statistical power and improves the likelihood of
                          reaching 90-95% confidence on sustained impact.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="overflow-hidden rounded-xl border border-gray-200" data-id="element-502">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-503">
                  <thead className="bg-gray-50" data-id="element-504">
                    <tr data-id="element-505">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-[#1E90FF] uppercase tracking-wider" data-id="element-506">
                        Dimension
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-507">
                        Traditional Survey
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-[#1E90FF] uppercase tracking-wider" data-id="element-508">
                        Brandpulse Resonance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-509">
                    <tr data-id="element-510">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-511">
                        Sample
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-512">
                        ~1,000 respondents
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-513">
                        279,000+ observations
                      </td>
                    </tr>
                    <tr className="bg-gray-50/50" data-id="element-514">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-515">
                        Method
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-516">
                        Self-reported recall
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-517">
                        Observed behavior
                      </td>
                    </tr>
                    <tr data-id="element-518">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-519">
                        Environment
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-520">
                        Survey panel
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-521">
                        Natural viewing environment
                      </td>
                    </tr>
                    <tr className="bg-gray-50/50" data-id="element-522">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-523">
                        Timing
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-524">
                        Post-campaign retrospective
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900" data-id="element-525">
                        Real-time + sustained tracking
                      </td>
                    </tr>
                    <tr data-id="element-526">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-527">
                        Certainty
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500" data-id="element-528">
                        Directional indication
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#1E90FF]" data-id="element-529">
                        97.5% statistical validation
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Quick Reference */}
              <div className="bg-gradient-to-br from-[#1E90FF]/5 to-blue-50 p-6 rounded-xl border border-[#1E90FF]/20" data-id="element-530">
                <h4 className="text-sm font-bold text-[#1E90FF] uppercase tracking-wide mb-4" data-id="element-531">
                  Quick Reference for Executives
                </h4>
                <ul className="space-y-3" data-id="element-532">
                  <li className="flex items-start space-x-2" data-id="element-533">
                    <CheckCircleIcon className="h-4 w-4 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-534" />
                    <span className="text-sm text-gray-700" data-id="element-535">
                      <span className="font-semibold" data-id="element-536">Measured:</span> Real
                      behavior in natural viewing environments
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-537">
                    <CheckCircleIcon className="h-4 w-4 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-538" />
                    <span className="text-sm text-gray-700" data-id="element-539">
                      <span className="font-semibold" data-id="element-540">Sample:</span> 279,000+
                      unique individuals
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-541">
                    <CheckCircleIcon className="h-4 w-4 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-542" />
                    <span className="text-sm text-gray-700" data-id="element-543">
                      <span className="font-semibold" data-id="element-544">Campaign Impact:</span>{' '}
                      +33% uplift (97.5% confidence)
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-545">
                    <CheckCircleIcon className="h-4 w-4 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-546" />
                    <span className="text-sm text-gray-700" data-id="element-547">
                      <span className="font-semibold" data-id="element-548">Sustained Effect:</span>{' '}
                      +14% post-flight (directional)
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-549">
                    <CheckCircleIcon className="h-4 w-4 text-[#1E90FF] mt-0.5 flex-shrink-0" data-id="element-550" />
                    <span className="text-sm text-gray-700" data-id="element-551">
                      <span className="font-semibold" data-id="element-552">Quality:</span> GDPR
                      compliant, premium publishers
                    </span>
                  </li>
                </ul>
              </div>

              {/* Common Questions */}
              <div data-id="element-553">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-554">
                  <HelpCircleIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-555" />
                  Common Questions
                </h3>
                <div className="space-y-4" data-id="element-556">
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-557">
                    <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-558">
                      How is this different from traditional brand lift studies?
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-559">
                      Traditional studies survey a small sample about what they
                      remember. Brandpulse observes the entire target audience's
                      actual behavior in real-time, measured in their natural
                      viewing environment. We measure what people do, not what
                      they claim to recall.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-560">
                    <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-561">
                      Can we trust behavioral data over surveys?
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-562">
                      Behavior reveals truth, surveys reveal memory. When
                      someone actively engages with your brand, that's a
                      stronger predictor of purchase intent than claimed
                      awareness weeks after a campaign. Actions speak louder
                      than words.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-563">
                    <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-564">
                      What if external factors influenced the results?
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-565">
                      Our three-phase design with audience exclusion isolates
                      your campaign's impact. By measuring the same target
                      demographic before, during, and after your TV flight,
                      while keeping the measurement stimulus constant, we
                      attribute behavioral changes to your media pressure.
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-566">
                      <span className="font-semibold text-gray-900" data-id="element-567">
                        Important context:
                      </span>{' '}
                      This is a real-world measurement, not a controlled lab
                      experiment. External factors (news events, competitor
                      campaigns, market conditions) are part of the reality we
                      measure. If your campaign shows no lift despite strong
                      creative, that's valuable intelligence, it tells you
                      external headwinds prevented breakthrough. Conversely,
                      strong lift despite external noise proves your campaign's
                      power.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};